import { ChangeEvent, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Box, Button, Text, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { AnimatePresence, motion } from 'motion/react';
import { NoteData } from '@/../../server/types/apiTypes';
import { NoteEntityType, PrivacyLevel } from '@/../../shared/types/notesTypes';
import defaultProfilePic from '@/assets/profileAnonPicInverse.png';
import NoteTextarea from '@/components/NoteTextarea';
import NoteItem from '@/components/Notes/NoteItem';
import { useNotesInfiniteQuery } from '@/hooks/useNotesInfiniteQuery';
import { useAppSelector } from '@/store/hooks';
import { sizing } from '@/styles/sizing';
import { getStatsigClient } from '@/utils/statsig';

export const Route = createFileRoute('/_privateRoutes/_mainNavBar/notes')({
  component: RouteComponent,
});

function RouteComponent() {
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isNotePublic, setIsNotePublic] = useState(true);
  const toast = useToast();
  const authState = useAppSelector((state) => state.auth);
  const profilePic = authState.profilePic;
  const navigate = useNavigate({ from: '/notes' });
  const setNoteContent = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setNewNoteContent(event.target.value);
    getStatsigClient().logEvent('note_typing_changed', event.target.value);
  };
  const [isLoading, setIsLoading] = useState(false);

  const handleSaveNote = async () => {
    try {
      if (newNoteContent.length === 0) {
        toast({
          title: 'Failed to save note',
          description: 'Your note cannot be empty',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom',
          variant: 'subtle',
          colorScheme: 'red',
        });
        return;
      }

      setIsLoading(true);

      let response = null;

      // Prepare the new note object based on the Sequelize model
      const newNote = {
        userId: authState.userId, // the user signed in and userId from localstorage,
        relatedAskId: null,
        //relatedOnboardingQuestionId: null,
        content: newNoteContent, // Ensure this variable contains the note content
        // embedding: embeddingData.embedding, handled in backend
        embeddingModel: null,
        tokenCount: null,
        tokenCost: null,
        status: 'ENABLED',
        isArchived: false,
        privacyLevel: isNotePublic ? PrivacyLevel.PUBLIC : PrivacyLevel.CONNECTION_ONLY,
        createDate: new Date().toISOString(),
        noteType: 'NOTE',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      response = await axios.post('/api/notes', newNote);

      setNewNoteContent('');
      getStatsigClient().logEvent('add_note', newNoteContent);

      setLocalNotes((prev) => [
        {
          id: response.data.noteId,
          title: 'You added a note',
          content: newNoteContent,
          entityType: NoteEntityType.NOTE,
          entityId: response.data.noteId,
          privacyLevel: isNotePublic ? PrivacyLevel.PUBLIC : PrivacyLevel.CONNECTION_ONLY,
          updatedAt: new Date(),
          isDeleted: false,
        },
        ...prev,
      ]);

      setIsLoading(false);

      toast({
        title: 'Note Added',
        description: 'Your note has been successfully saved.',
        status: 'success',
        duration: 1500,
        isClosable: true,
        position: 'bottom',
        render: () => (
          <Box color="white" p={3} bg="#1C1C1C">
            <Text fontWeight="bold">Note Added</Text>
            <Text>Your note has been successfully saved.</Text>
          </Box>
        ),
      });
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };

  const { data } = useNotesInfiniteQuery();
  const [localNotes, setLocalNotes] = useState<NoteData[]>([]);

  const notes = useMemo(() => {
    return localNotes.concat(data?.pages[0].list ?? []).slice(0, 5);
  }, [data, localNotes]);

  return (
    <div className="mt-8 px-4">
      <NoteTextarea
        profilePic={profilePic ?? defaultProfilePic}
        noteContent={newNoteContent}
        setNoteContent={setNoteContent}
        onSaveNote={handleSaveNote}
        isNotePublic={isNotePublic}
        setIsNotePublic={setIsNotePublic}
        isResizable={false}
      />
      <AnimatePresence>
        {newNoteContent.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="mt-4"
          >
            <Button
              width="100%"
              maxW={sizing.maxAppContentWidthPx}
              mx="auto"
              onClick={handleSaveNote}
              colorScheme="dotsRed"
              variant="solid"
              height="60px"
              borderRadius="full"
              fontSize={{ base: 'sm', md: 'md', lg: 'lg' }}
              isLoading={isLoading}
            >
              Save
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.div layout className="flex flex-col gap-3 h-[calc(100vh-420px)] overflow-y-auto">
        <div
          className="text-[#B2B2B2] text-xs font-bold self-end mr-4 mt-4 cursor-pointer"
          onClick={() => navigate({ to: '/notes/list' })}
        >
          See more
        </div>
        {notes.map((note) => (
          <NoteItem key={note.id} {...note} />
        ))}
      </motion.div>
    </div>
  );
}
